import { Alert, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from '../../contexts/FormContext';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { useFormData } from '../../hooks/useFormValues';
import { Server } from '../../services/Server';
import { handleError } from '../../utils/Error';
import { cleanFormDataForSave, cleanUserPatterns } from '../../utils/utils';

// Inputs are only committed (saved, which relaunches the optimization) once the user stopped editing.
const COMMIT_DELAY_MS = 1000;
const DATE_FORMAT = 'YYYY-MM-DD';

export type ScheduleOptimizationValues = { start_date: string; current_soiling_ratio: number };

const isValidStartDate = (date: Dayjs | null) => !!date && date.isValid() && date.year() >= 2000 && date.year() <= 2100;

const isValidSoilingRatio = (value: string) => value !== '' && Number(value) > 0 && Number(value) <= 1;

/** Committed (persisted) schedule optimization inputs, or null while they are missing. */
export const getScheduleOptimizationValues = (formData: Record<string, unknown> | null | undefined): ScheduleOptimizationValues | null => {
  const values = formData?.schedule_optimization as Partial<ScheduleOptimizationValues> | undefined;
  if (!values?.start_date || values?.current_soiling_ratio === undefined || values?.current_soiling_ratio === null) {
    return null;
  }
  return values as ScheduleOptimizationValues;
};

type Props = {
  project_id: string;
};

const ScheduleOptimizationInputs = ({ project_id }: Props) => {
  const formData = useFormData();
  const { getValues, setFormData } = useFormContext();
  const { putProjectAPI, getCurrentSoilingRatioAPI } = Server();
  const saved = formData?.schedule_optimization;

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs(saved?.start_date ?? undefined));
  const [soilingRatio, setSoilingRatio] = useState<string>(saved?.current_soiling_ratio?.toString() ?? '');

  // The suggested current soiling ratio is always displayed; it is only committed while none has been saved yet.
  const needsDefault = saved?.current_soiling_ratio === undefined || saved?.current_soiling_ratio === null;
  const { data: defaultSoilingRatio, isFetching: isFetchingDefault } = useQuery({
    queryKey: ['current-soiling-ratio', project_id],
    queryFn: () => getCurrentSoilingRatioAPI({ project_id }),
    gcTime: 0,
    retry: false,
  });
  const suggestedSoilingRatio =
    defaultSoilingRatio?.current_soiling_ratio === undefined || defaultSoilingRatio?.current_soiling_ratio === null
      ? null
      : (Math.round(defaultSoilingRatio.current_soiling_ratio * 10000) / 10000).toString();
  const suggestionHelperText =
    suggestedSoilingRatio === null
      ? 'No suggestion available: no soiling sensor curve is selected in the Soiling Ratio tab'
      : `Based on the sensor curves that you selected we suggest ${suggestedSoilingRatio}`;

  const commit = useCallback(
    async (start_date: Dayjs | null, current_soiling_ratio: string) => {
      if (!isValidStartDate(start_date) || !isValidSoilingRatio(current_soiling_ratio)) {
        return;
      }
      const newFormData = {
        ...getValues(),
        schedule_optimization: {
          start_date: start_date!.format(DATE_FORMAT),
          current_soiling_ratio: Number(current_soiling_ratio),
        },
      };
      try {
        // Save first: the backend reads the inputs from the persisted project.
        await putProjectAPI(project_id, cleanUserPatterns(cleanFormDataForSave(newFormData)));
        setFormData(newFormData);
      } catch (error) {
        handleError(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project_id],
  );

  const debouncedCommit = useDebouncedCallback(commit, COMMIT_DELAY_MS, [commit]);

  // First opening: commit the defaults (today, average current soiling ratio of the selected sensor curves).
  useEffect(() => {
    if (needsDefault && suggestedSoilingRatio !== null) {
      setSoilingRatio(suggestedSoilingRatio);
      commit(startDate, suggestedSoilingRatio);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSoilingRatio]);

  const noDefault = needsDefault && !isFetchingDefault && defaultSoilingRatio?.current_soiling_ratio === null;

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid size={6}>
        <DatePicker
          label="Starting Date"
          value={startDate}
          format={DATE_FORMAT}
          minDate={dayjs('2000-01-01')}
          maxDate={dayjs('2100-12-31')}
          onChange={(value) => {
            setStartDate(value);
            debouncedCommit(value, soilingRatio);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: !isValidStartDate(startDate),
              helperText: isValidStartDate(startDate) ? 'First day of the forecast' : 'Please enter a valid date',
            },
          }}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          type="number"
          label="Current Soiling Ratio (0-1)"
          value={soilingRatio}
          disabled={needsDefault && isFetchingDefault}
          inputProps={{ min: 0, max: 1, step: 0.01 }}
          onChange={(e) => {
            setSoilingRatio(e.target.value);
            debouncedCommit(startDate, e.target.value);
          }}
          error={!isFetchingDefault && !isValidSoilingRatio(soilingRatio)}
          helperText={
            isFetchingDefault
              ? 'Computing the suggested value from the selected sensor curves...'
              : isValidSoilingRatio(soilingRatio)
              ? `Soiling ratio on the starting date. ${suggestionHelperText}`
              : `Please enter a value between 0 (excluded) and 1. ${suggestionHelperText}`
          }
        />
      </Grid>
      {noDefault ? (
        <Grid size={12}>
          <Alert severity="warning">
            No soiling sensor curve is selected in the Soiling Ratio tab: please enter the current soiling ratio.
          </Alert>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default ScheduleOptimizationInputs;
